import { useEffect, useRef } from "react";

import disneyLogoBlack from "../../assets/images/copyrightFooter/disneyLogoBlack.svg";
import disneyLogoWhite from "../../assets/images/copyrightFooter/disneyLogoWhite.svg";
import Text from "../ui/Text";
import { mobileMaxWidth } from "../../constants/layout";
import useStore from "../../hooks/store/useStore";
import { SourceType } from "../../types";
import cn from "../../utils/cn";

const copyrightFooterOffsetVar = "--copyright-footer-offset";
const scrollEventOptions: AddEventListenerOptions = {
  passive: true,
  capture: true,
};

export default function CopyrightFooter({
  isWhite = false,
  className,
}: {
  isWhite?: boolean;
  className?: string;
}) {
  const textRef = useRef<HTMLDivElement>(null);
  const { source } = useStore((state) => state.global);
  const isWeb = source === SourceType.Web;

  useEffect(() => {
    if (source !== SourceType.ClubApp) return;

    const copyrightText = textRef.current;
    if (!copyrightText) return;

    const updateCopyrightFooterOffset = () => {
      const distanceFromViewportBottom = Math.max(
        0,
        window.innerHeight - copyrightText.getBoundingClientRect().top,
      );

      document.documentElement.style.setProperty(
        copyrightFooterOffsetVar,
        `${distanceFromViewportBottom}px`,
      );
    };

    const resizeObserver = new ResizeObserver(updateCopyrightFooterOffset);

    updateCopyrightFooterOffset();
    resizeObserver.observe(document.body);
    window.addEventListener(
      "scroll",
      updateCopyrightFooterOffset,
      scrollEventOptions,
    );
    window.addEventListener("resize", updateCopyrightFooterOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener(
        "scroll",
        updateCopyrightFooterOffset,
        scrollEventOptions,
      );
      window.removeEventListener("resize", updateCopyrightFooterOffset);
      document.documentElement.style.removeProperty(copyrightFooterOffsetVar);
    };
  }, [source]);

  const textColor = isWhite ? "text-white" : "text-[#231815]";
  const disneyLogo = isWhite ? disneyLogoWhite : disneyLogoBlack;
  const textStyle = cn("text-[11px] leading-[14px]", textColor);

  return (
    <div
      className={cn(
        "mx-auto w-full px-4 pb-2 pt-16",
        isWeb && "pb-[60px]",
        className,
      )}
      style={{ maxWidth: mobileMaxWidth }}
    >
      <div ref={textRef} className="flex w-full flex-col gap-2">
        <div className="flex items-center gap-[11px]">
          <Text normal className={textStyle}>
            ©Disney
          </Text>
          <img src={disneyLogo} alt="" />
          <Text normal className={textStyle}>
            ©Disney/Pixar
          </Text>
        </div>

        <Text normal className={textStyle}>
          {
            "©Disney. Based on the “Winnie the Pooh” Works by/A.A. Milne and E.H Shepard."
          }
        </Text>
      </div>
    </div>
  );
}
